import * as pc from 'playcanvas';

import { DeviceLook } from './device-look';
import type { EdgeLookConfig } from './walk-controller';

/**
 * Rotating-mode camera controller — orbits around a target point.
 *
 * Inputs (mirrors the editor's orbit feel and the legacy viewer/'s sensitivity):
 *  - Left-mouse drag, single-finger drag: rotate (azimuth + elevation)
 *  - Mouse wheel, two-finger pinch:       zoom (distance to target)
 *  - No translation by user.
 *
 * Camera state is parameterised as (target, azimuth, elevation, distance) and
 * recomputed into a Cartesian position every frame. Tweens are smoothed via
 * exponential decay (`damping`) — drag the mouse, release, the camera coasts
 * to a stop. Same `0.003 rad/px` sensitivity as the legacy viewer so the
 * feel transfers.
 *
 * Distance is clamped between `minDistance` and `maxDistance` (callers should
 * derive these from scene bounds; default is a generous fixed range).
 *
 * Elevation is clamped to ±89° so the look-at math doesn't flip at the pole.
 */
/**
 * Keyboard orbit (the A/B experiment's orbit scheme B). WASD/arrows steer the
 * orbit: A/← and D/→ turn, W/↑ raises the camera (looking further down),
 * S/↓ lowers it. Speeds are rad/s at full press;
 * input eases in/out over ~an eighth of a second so taps nudge and holds
 * glide. Null (the default) = shipped behaviour, no key handling at all.
 */
export interface KeyOrbitConfig {
    yawSpeed: number;
    pitchSpeed: number;
}

/**
 * Held zoom (orbit schemes D and E): while the input is held the distance
 * multiplies by `rate` per second zooming out (divides zooming in) — the
 * same exponential curve the wheel uses, spread over time.
 */
export interface ZoomHoldConfig {
    rate: number;
}

/**
 * Scroll-to-orbit (scheme F): a plain two-finger scroll steers the orbit —
 * horizontal turns, vertical tilts — in rad per scroll px. A trackpad pinch
 * (which arrives as a ctrlKey wheel event) keeps zooming.
 */
export interface ScrollOrbitConfig {
    yawPerPx: number;
    pitchPerPx: number;
}

/**
 * Pointer-lock orbit (scheme G): a click captures the mouse (the cursor
 * disappears), raw motion steers the orbit with no screen edge to ratchet
 * against, Esc — the browser's own exit — releases. rad per counted px.
 */
export interface PointerOrbitConfig {
    sens: number;
}

/** Device-orientation orbit (mobile scheme B): turning the device walks the
 *  camera around the pivot, like holding a model. Gains of 1 = one-to-one. */
export interface GyroOrbitConfig {
    yawGain: number;
    pitchGain: number;
}

export interface OrbitOptions {
    target?: pc.Vec3;
    distance?: number;
    azimuth?: number;     // radians, 0 = looking down -Z
    elevation?: number;   // radians, 0 = horizontal
    minDistance?: number;
    maxDistance?: number;
    minElevation?: number;        // radians; floor for elevation (e.g. +16° keeps the camera above ground)
    rotateSensitivity?: number;   // radians per pixel
    zoomSensitivity?: number;     // multiplicative per wheel notch
    damping?: number;             // 0 = no smoothing, ~0.9 = silky
}

const DEFAULTS: Required<OrbitOptions> = {
    target: new pc.Vec3(0, 0, 0),
    distance: 4,
    azimuth: 0,
    elevation: Math.PI / 8,            // ~22°, slightly looking down
    minDistance: 0.2,
    maxDistance: 100,
    minElevation: -(89 * Math.PI) / 180,  // unrestricted by default (full ±89° range)
    rotateSensitivity: 0.003,
    zoomSensitivity: 1.1,
    damping: 0.85
};

const ELEV_CLAMP = (89 * Math.PI) / 180;

// Tap-vs-drag dead-zone: a pointer must travel this many CSS px before it counts
// as a drag (rotate). Below it the gesture is a tap and the camera holds still —
// fixes the "camera jumps the instant you touch the screen" problem, since a
// touch tap always carries a few px of finger jitter between down and up.
const DRAG_THRESHOLD_PX = 8;
const DRAG_THRESHOLD_SQ = DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX;
// Hold-the-edge: a finger still (under the drag threshold) for this long is
// a hold, not the start of a swipe.
const HOLD_MS = 250;
// How fast lining up with the device (scheme start, recenter) closes the gap,
// per second: ~90% in 0.4 s.
const GYRO_ALIGN_RATE = 6;

export class OrbitController {

    readonly camera: pc.Entity;

    readonly target: pc.Vec3;

    distance: number;

    azimuth: number;

    elevation: number;

    minDistance: number;

    maxDistance: number;

    // Elevation floor in radians. Drone/exterior scenes set this positive
    // (e.g. +16°) so the camera can never dip below the ground plane.
    minElevation: number;

    // Idle auto-rotation: after `autoRotateDelay` seconds without input the
    // camera slowly orbits the pivot at `autoRotateSpeed` rad/s (ramping in
    // over ~2s so it never jumps). Any interaction stops it instantly.
    autoRotate = false;

    autoRotateSpeed = (3 * Math.PI) / 180;  // rad/s (3°/s ≈ 2 min per revolution)

    autoRotateDelay = 5;                    // seconds of inactivity before starting

    private lastInteraction = performance.now();

    rotateSensitivity: number;

    zoomSensitivity: number;

    damping: number;

    // Velocity for smooth coasting.
    private azimuthVel = 0;

    private elevationVel = 0;

    private distanceVel = 0;

    // Drag tracking.
    private dragging = false;

    // Gesture attribution for the practice tutorial: set ONLY by real user
    // input (drag rotation / wheel / pinch) — never by idle auto-rotation or
    // the soft-return easing, so the practice card can't check itself off.
    gestureRotate = false;

    gestureZoom = false;

    gestureKeys = false;

    gestureEdge = false;

    // ── Experiment seams (schemes B–E). Every default is the shipped
    // behaviour, so a controller nobody reconfigures is unchanged. ──
    // Keyboard orbit (schemes B and E). Null = the key listeners are
    // registered but return immediately.
    private keyOrbit: KeyOrbitConfig | null = null;

    private keysDown = new Set<string>();

    private keyYawVel = 0;

    private keyPitchVel = 0;

    // Drag rotation on/off (schemes C and D turn it off).
    private dragRotate = true;

    // Wheel/pinch zoom on/off (schemes D and E replace it).
    private wheelZoom = true;

    // Edge steering (schemes C and D): the cursor resting in a screen margin
    // drives azimuth/elevation like B's keys do. Same config shape as the
    // walk controller's hover-look so the margins feel identical.
    private edgeOrbit: EdgeLookConfig | null = null;

    private mouseX = 0;

    private mouseY = 0;

    private mouseIn = false;

    private hoverTravel = 0;     // px the cursor moved since the last frame

    private hoverSpeed = 0;      // px/s, smoothed — a fast cursor suppresses the turn

    private edgeAzVel = 0;

    private edgeElVel = 0;

    // W/S held zoom (scheme D).
    private keyZoom: ZoomHoldConfig | null = null;

    // Spacebar toggle-hold zoom (scheme E): each press flips the direction,
    // holding zooms that way. Starts at +1 (out) so the FIRST press flips to
    // in — zooming toward the scene is the natural first move.
    private spaceZoom: ZoomHoldConfig | null = null;

    private spaceHeld = false;

    private spaceDir = 1;

    // Scroll steering (scheme F).
    private scrollOrbit: ScrollOrbitConfig | null = null;

    gestureScroll = false;

    // Pointer-lock orbit (scheme G).
    private pointerOrbit: PointerOrbitConfig | null = null;

    private lockActive = false;

    // ── Mobile scheme capabilities (registry #4). Defaults = shipped. ──
    // Device-orientation orbit (mobile B).
    private gyroOrbit: GyroOrbitConfig | null = null;

    private readonly gyro = new DeviceLook();

    /** The pose a recenter returns to: the view when the gyro lined up,
     *  which the device held upright maps onto. */
    private gyroHomeAz = 0;

    private gyroHomeEl = 0;

    /** Rad still to ease in toward the device (scheme start, recenter). */
    private gyroAzOwed = 0;

    private gyroElOwed = 0;

    // Hold-the-edge (mobile C): a finger resting in a margin steers like the
    // desktop cursor does. The hold is decided after HOLD_MS of stillness.
    private edgeHold: EdgeLookConfig | null = null;

    private holdActive = false;

    private downT = 0;

    // Double-tap detector (mobile B: recenter the gyro).
    private lastTapT = -1e9;

    private lastTapX = 0;

    private lastTapY = 0;

    private canvas: HTMLCanvasElement | null = null;

    // Pointer is down and a candidate for a drag, but hasn't crossed the
    // dead-zone yet — a tap stays in this state and never rotates.
    private armed = false;

    private downX = 0;

    private downY = 0;

    private lastPointerX = 0;

    private lastPointerY = 0;

    // Touch pinch tracking.
    private pinchDistance = 0;

    // Live touch count — while ≥2 fingers are down the pointer handlers must
    // stand down entirely, else the two fingers' alternating pointermoves
    // re-arm the drag mid-pinch and rotation garbage fights the zoom.
    private activeTouches = 0;

    // Soft-return mode: when an EXTERNAL seed (tour exit) leaves the camera
    // outside the authored zoom band / pitch floor, don't snap — keep the pose
    // and EASE back into bounds over ~a second. User zoom/drag input behaves
    // normally throughout; hard clamps resume once back in range.
    private softReturn = false;

    // Cleanup handlers.
    private cleanupFns: Array<() => void> = [];

    constructor(camera: pc.Entity, opts: OrbitOptions = {}) {
        this.camera = camera;
        const o = { ...DEFAULTS, ...opts };
        this.target = o.target.clone();
        this.distance = o.distance;
        this.azimuth = o.azimuth;
        this.elevation = o.elevation;
        this.minDistance = o.minDistance;
        this.maxDistance = o.maxDistance;
        this.minElevation = o.minElevation;
        this.rotateSensitivity = o.rotateSensitivity;
        this.zoomSensitivity = o.zoomSensitivity;
        this.damping = o.damping;
    }

    /**
     * Derive a sensible initial orbit pose from a starting camera position +
     * target. Lets callers pass a scene's `camera.initial` straight in.
     */
    initFromPose(position: pc.Vec3, target: pc.Vec3): void {
        this.target.copy(target);
        const dx = position.x - target.x;
        const dy = position.y - target.y;
        const dz = position.z - target.z;
        this.distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
        const horiz = Math.sqrt(dx * dx + dz * dz);
        this.elevation = Math.atan2(dy, horiz);
        // PlayCanvas uses right-handed Y-up. Az 0 = +Z, increases toward +X.
        this.azimuth = Math.atan2(dx, dz);
        // If this seed lands outside the authored bounds (a tour can travel
        // anywhere), don't snap to them — ease back over ~a second instead.
        const elevFloor = Math.max(this.minElevation, -ELEV_CLAMP);
        this.softReturn = this.distance < this.minDistance || this.distance > this.maxDistance ||
            this.elevation < elevFloor || this.elevation > ELEV_CLAMP;
    }

    /**
     * Auto-fit zoom limits to the scene's bounding sphere radius. Call after
     * the scene has finished loading so bounds are valid.
     */
    fitToBounds(radius: number): void {
        // Generous zoom range — the previous 5% of scene radius floor was
        // too aggressive on big drone scenes (50-100m radius blocked the
        // user from zooming within 5m of the pivot). Use an absolute 10cm
        // floor so the user can always get close enough to inspect detail.
        this.minDistance = 0.1;
        this.maxDistance = Math.max(20, radius * 10);
        this.distance = Math.min(Math.max(this.distance, this.minDistance), this.maxDistance);
    }

    // ── Experiment setters. All configs survive detach/attach, so a tour
    // pause doesn't lose the scheme. ──

    /** Enable/disable keyboard orbit (schemes B and E). */
    setKeyOrbit(cfg: KeyOrbitConfig | null): void {
        this.keyOrbit = cfg;
        if (!cfg) {
            this.keyYawVel = 0;
            this.keyPitchVel = 0;
        }
    }

    /** Enable/disable drag rotation (schemes C and D remove it). */
    setDragRotateEnabled(on: boolean): void {
        this.dragRotate = on;
        if (!on) {
            this.armed = false;
            this.dragging = false;
        }
    }

    /** Enable/disable wheel + trackpad-pinch zoom (schemes D and E replace it). */
    setWheelZoomEnabled(on: boolean): void {
        this.wheelZoom = on;
    }

    /** Enable/disable margin steering (schemes C and D). */
    setEdgeOrbit(cfg: EdgeLookConfig | null): void {
        this.edgeOrbit = cfg;
        if (!cfg) {
            this.edgeAzVel = 0;
            this.edgeElVel = 0;
            this.hoverTravel = 0;
            this.hoverSpeed = 0;
        }
    }

    /** Enable/disable W/S held zoom (scheme D). */
    setKeyZoom(cfg: ZoomHoldConfig | null): void {
        this.keyZoom = cfg;
    }

    /** Enable/disable scroll steering (scheme F). */
    setScrollOrbit(cfg: ScrollOrbitConfig | null): void {
        this.scrollOrbit = cfg;
    }

    /** Enable/disable pointer-lock orbit (scheme G). Switching away must hand
     *  the cursor back. */
    setPointerOrbit(cfg: PointerOrbitConfig | null): void {
        this.pointerOrbit = cfg;
        if (!cfg) this.releasePointerLock();
    }

    private releasePointerLock(): void {
        try {
            if (this.canvas && document.pointerLockElement === this.canvas) {
                document.exitPointerLock();
            }
        } catch { /* no pointer-lock API (old browser) — nothing to release */ }
    }

    // ── Mobile scheme setters ──────────────────────────────────────────────

    /** Device-orientation orbit. First call must come from a user gesture
     *  (iOS permission) — the scheme tile's tap is one. */
    setGyroOrbit(cfg: GyroOrbitConfig | null): void {
        this.gyroOrbit = cfg;
        this.gyroAzOwed = 0;
        this.gyroElOwed = 0;
        if (cfg) this.gyro.start();
        else this.gyro.stop();
    }

    /** Gyro scheme's double-tap: back to the pose the gyro lined up on, with
     *  the device's current tilt applied on top of it. */
    private recenterGyro(): void {
        const devicePitch = this.gyro.pitch();
        if (!this.gyroOrbit || devicePitch === null) return;
        this.azimuthVel = 0;
        this.elevationVel = 0;
        const turn = this.gyroHomeAz - this.azimuth;
        this.gyroAzOwed = turn - 2 * Math.PI * Math.round(turn / (2 * Math.PI));
        this.gyroElOwed = this.gyroHomeEl - devicePitch * this.gyroOrbit.pitchGain - this.elevation;
        this.lastInteraction = performance.now();
    }

    /** Hold-the-edge steering (mobile C). */
    setEdgeHold(cfg: EdgeLookConfig | null): void {
        this.edgeHold = cfg;
        if (!cfg) {
            this.holdActive = false;
            this.edgeAzVel = 0;
            this.edgeElVel = 0;
        }
    }

    /** Enable/disable spacebar toggle-hold zoom (scheme E). */
    setSpaceZoom(cfg: ZoomHoldConfig | null): void {
        this.spaceZoom = cfg;
        if (!cfg) {
            this.spaceHeld = false;
            this.spaceDir = 1;
        }
    }

    /** Attach DOM listeners to the canvas. Idempotent — calling twice no-ops. */
    attach(canvas: HTMLCanvasElement): void {
        if (this.cleanupFns.length > 0) return;
        this.canvas = canvas;
        // The device kept turning while detached (tour, panel) — drop that
        // delta or the view snaps on resume.
        this.gyro.consume();

        const onPointerDown = (e: PointerEvent) => {
            // Schemes C/D: no drag — the pointer never arms, so a press can't
            // rotate and can't suppress the edge steering.
            if (!this.dragRotate) return;
            // Left button only for mouse; any button for pen/touch
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            // A pinch in progress owns the gesture — don't arm a drag.
            if (this.activeTouches >= 2 || this.pinchDistance > 0) return;
            this.lastInteraction = performance.now();
            // Arm a potential drag but DON'T rotate yet: a tap (finger jitter
            // under the dead-zone) must leave the camera still. Rotation begins
            // in onPointerMove only once the pointer crosses DRAG_THRESHOLD_PX.
            this.armed = true;
            this.dragging = false;
            this.downT = e.timeStamp;
            this.downX = e.clientX;
            this.downY = e.clientY;
            this.lastPointerX = e.clientX;
            this.lastPointerY = e.clientY;
            canvas.setPointerCapture(e.pointerId);
            // Cancel residual coast so a fresh interaction feels immediate.
            this.azimuthVel = 0;
            this.elevationVel = 0;
        };
        const onPointerMove = (e: PointerEvent) => {
            // Pointer-lock orbit (scheme G): while captured, raw motion IS the
            // input — nothing else may see these events. movementX/Y spikes on
            // lock transitions in some browsers; clamp so a jolt can't
            // teleport the view. Horizontal sign matches the DRAG (mouse
            // right → the camera orbits right, azimuth decreases): the
            // locked mouse must spin the scene the way a swipe does (Lenny,
            // 13/10/2026 — it ran the other way). Vertical as it was:
            // mouse up ≡ W (the camera rises).
            if (this.lockActive && this.pointerOrbit) {
                const mx = Math.max(-120, Math.min(120, e.movementX));
                const my = Math.max(-120, Math.min(120, e.movementY));
                this.azimuth -= mx * this.pointerOrbit.sens;
                this.elevation += -my * this.pointerOrbit.sens;
                this.lastInteraction = performance.now();
                // Real mouse rotation — arms the look row like a drag would.
                this.gestureRotate = true;
                return;
            }
            // Hover tracking for the edge steering — runs for every mouse
            // move, drag or not, exactly like the walk controller's.
            if (e.pointerType !== 'touch') {
                if (this.edgeOrbit && this.mouseIn) {
                    this.hoverTravel += Math.abs(e.clientX - this.mouseX) + Math.abs(e.clientY - this.mouseY);
                }
                this.mouseX = e.clientX;
                this.mouseY = e.clientY;
                this.mouseIn = true;
            }
            if (!this.armed || this.activeTouches >= 2 || this.pinchDistance > 0) return;
            this.lastInteraction = performance.now();
            // Tap-vs-drag: ignore movement until it crosses the dead-zone so a
            // tap never rotates. Once crossed, start the drag from the CURRENT
            // point (no jump from the consumed dead-zone distance).
            if (!this.dragging) {
                const ddx = e.clientX - this.downX;
                const ddy = e.clientY - this.downY;
                if (ddx * ddx + ddy * ddy < DRAG_THRESHOLD_SQ) return;
                this.dragging = true;
                this.holdActive = false;   // a hold that moves is a swipe
                this.lastPointerX = e.clientX;
                this.lastPointerY = e.clientY;
            }
            const dx = e.clientX - this.lastPointerX;
            const dy = e.clientY - this.lastPointerY;
            this.lastPointerX = e.clientX;
            this.lastPointerY = e.clientY;
            // Convert drag to angular velocity. Damped each frame in update().
            // Sign convention (matches Blender / Google Earth / the legacy viewer):
            //   drag-right  → camera orbits right around target  (az decreases)
            //   drag-down   → camera moves UP, looks DOWN on target (el increases)
            this.azimuthVel = -dx * this.rotateSensitivity;
            this.elevationVel = dy * this.rotateSensitivity;
            this.azimuth += this.azimuthVel;
            this.elevation += this.elevationVel;
            this.gestureRotate = true;
        };
        const onPointerUp = (e: PointerEvent) => {
            if (canvas.hasPointerCapture(e.pointerId)) {
                canvas.releasePointerCapture(e.pointerId);
            }
            // Double-tap (mobile B: recenter the gyro): two quick, still taps
            // close together in time and space.
            if (this.gyroOrbit && this.armed && !this.dragging && !this.holdActive &&
                e.timeStamp - this.downT < 300) {
                const dt = e.timeStamp - this.lastTapT;
                const dd = Math.hypot(e.clientX - this.lastTapX, e.clientY - this.lastTapY);
                if (dt < 350 && dd < 30) {
                    this.recenterGyro();
                    this.lastTapT = -1e9;
                } else {
                    this.lastTapT = e.timeStamp;
                    this.lastTapX = e.clientX;
                    this.lastTapY = e.clientY;
                }
            }
            this.armed = false;
            this.dragging = false;
            this.holdActive = false;
            if (e.pointerType === 'touch') this.mouseIn = false;
        };
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            // Normalise to pixels (deltaMode: 0=px, 1=lines, 2=pages).
            const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1;
            // Scroll steering (scheme F): a plain scroll turns/tilts; a
            // trackpad pinch arrives as a ctrlKey wheel event and falls
            // through to the zoom below. Signs picked so that, with macOS
            // natural scrolling, fingers-right turns right (≡ D) and
            // fingers-up raises the camera (≡ W) — both feel dials.
            if (this.scrollOrbit && !e.ctrlKey) {
                this.lastInteraction = performance.now();
                this.azimuth += -e.deltaX * unit * this.scrollOrbit.yawPerPx;
                this.elevation += e.deltaY * unit * this.scrollOrbit.pitchPerPx;
                this.gestureScroll = true;
                return;
            }
            // Schemes D/E zoom another way; still swallow the event so the
            // page never scrolls or browser-zooms behind the viewer.
            if (!this.wheelZoom) return;
            this.lastInteraction = performance.now();
            // Zoom proportionally to the gesture, not per event: a mouse notch
            // is ONE event of ~100px, but a trackpad swipe is a STREAM of tiny
            // deltas (2-20px each) — applying a full zoom step per event made
            // trackpads wildly oversensitive. Scale the exponent so one mouse
            // notch (100px) applies exactly `zoomSensitivity`, same as before.
            this.distance *= Math.pow(this.zoomSensitivity, (e.deltaY * unit) / 100);
            this.gestureZoom = true;
        };

        // Pinch handler (touch only). Uses TouchEvent because PointerEvents don't
        // give us multi-finger distance cleanly across browsers.
        const onTouchStart = (e: TouchEvent) => {
            this.lastInteraction = performance.now();
            this.activeTouches = e.touches.length;
            if (e.touches.length === 2) {
                const dx = e.touches[0].clientX - e.touches[1].clientX;
                const dy = e.touches[0].clientY - e.touches[1].clientY;
                this.pinchDistance = Math.sqrt(dx * dx + dy * dy);
                // Pinch owns the gesture — stand the drag machinery down so the
                // fingers' pointermoves can't re-arm rotation mid-pinch.
                this.armed = false;
                this.dragging = false;
                this.holdActive = false;
            }
        };
        const onTouchMove = (e: TouchEvent) => {
            this.lastInteraction = performance.now();
            if (e.touches.length === 2 && this.pinchDistance > 0) {
                e.preventDefault();
                const dx = e.touches[0].clientX - e.touches[1].clientX;
                const dy = e.touches[0].clientY - e.touches[1].clientY;
                const d = Math.sqrt(dx * dx + dy * dy);
                const ratio = this.pinchDistance / d;
                this.distance *= ratio;
                this.pinchDistance = d;
                this.gestureZoom = true;
                // While pinching, suppress the single-finger drag rotation
                this.dragging = false;
            }
        };
        const onTouchEnd = (e: TouchEvent) => {
            this.activeTouches = e.touches.length;
            this.pinchDistance = 0;
        };

        // Cursor left the canvas — the edge steering must stop turning.
        const onLeave = () => { this.mouseIn = false; };

        // Pointer lock (scheme G): a click is the only way in — browsers
        // demand a user gesture. Chrome returns a Promise that REJECTS if the
        // request races an exit (Esc then a fast re-click); Safari returns
        // undefined — guard both. Inert while pointerOrbit is null.
        const onClick = () => {
            if (!this.pointerOrbit || this.lockActive) return;
            try {
                void Promise.resolve(canvas.requestPointerLock()).catch(() => { /* transient — click again */ });
            } catch { /* no pointer-lock API — scheme degrades to nothing */ }
        };
        const onLockChange = () => {
            this.lockActive = document.pointerLockElement === canvas;
        };

        // Keyboard (schemes B, D, E). Same key set and lowercase convention
        // as WalkController. Inert while no key scheme is configured, so the
        // shipped orbit viewer never reacts to the keyboard. Gesture flags
        // are stamped in update(), where a key's EFFECT is known — in D the
        // W key zooms, and must not arm the "look" tutorial row.
        const KEYS = ['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'];
        const onKeyDown = (e: KeyboardEvent) => {
            if (this.spaceZoom && e.key === ' ') {
                // Space scrolls the page / clicks a focused button — swallow
                // it (repeats included). Direction flips on the PRESS only:
                // auto-repeat must not strobe in/out while held.
                e.preventDefault();
                if (!e.repeat) {
                    this.spaceDir = -this.spaceDir;
                    this.spaceHeld = true;
                }
                this.lastInteraction = performance.now();
                return;
            }
            if (!this.keyOrbit && !this.keyZoom) return;
            const k = e.key.toLowerCase();
            if (!KEYS.includes(k)) return;
            this.keysDown.add(k);
            this.lastInteraction = performance.now();
        };
        const onKeyUp = (e: KeyboardEvent) => {
            if (e.key === ' ') this.spaceHeld = false;
            this.keysDown.delete(e.key.toLowerCase());
        };
        // Alt-tab away mid-press and the keyup lands on another window — clear
        // everything or the orbit spins (or zooms) forever.
        const onBlur = () => {
            this.keysDown.clear();
            this.spaceHeld = false;
        };

        canvas.addEventListener('pointerdown', onPointerDown);
        canvas.addEventListener('pointermove', onPointerMove);
        canvas.addEventListener('pointerup', onPointerUp);
        canvas.addEventListener('pointercancel', onPointerUp);
        canvas.addEventListener('wheel', onWheel, { passive: false });
        canvas.addEventListener('touchstart', onTouchStart, { passive: true });
        canvas.addEventListener('touchmove', onTouchMove, { passive: false });
        canvas.addEventListener('touchend', onTouchEnd);
        canvas.addEventListener('contextmenu', (e) => e.preventDefault());
        canvas.addEventListener('pointerleave', onLeave);
        canvas.addEventListener('click', onClick);
        document.addEventListener('pointerlockchange', onLockChange);
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        window.addEventListener('blur', onBlur);

        this.cleanupFns.push(
            () => canvas.removeEventListener('pointerleave', onLeave),
            () => canvas.removeEventListener('click', onClick),
            () => document.removeEventListener('pointerlockchange', onLockChange),
            () => window.removeEventListener('keydown', onKeyDown),
            () => window.removeEventListener('keyup', onKeyUp),
            () => window.removeEventListener('blur', onBlur),
            () => canvas.removeEventListener('pointerdown', onPointerDown),
            () => canvas.removeEventListener('pointermove', onPointerMove),
            () => canvas.removeEventListener('pointerup', onPointerUp),
            () => canvas.removeEventListener('pointercancel', onPointerUp),
            () => canvas.removeEventListener('wheel', onWheel),
            () => canvas.removeEventListener('touchstart', onTouchStart),
            () => canvas.removeEventListener('touchmove', onTouchMove),
            () => canvas.removeEventListener('touchend', onTouchEnd)
        );
    }

    /** Remove DOM listeners. */
    detach(): void {
        // Before the canvas ref is dropped: a detach (tour start, panel open)
        // must hand the cursor back.
        this.releasePointerLock();
        this.lockActive = false;
        for (const fn of this.cleanupFns) fn();
        this.cleanupFns = [];
        // Keys can't key-up once the listeners are gone.
        this.keysDown.clear();
        this.keyYawVel = 0;
        this.keyPitchVel = 0;
        this.spaceHeld = false;
        this.mouseIn = false;
        this.hoverTravel = 0;
        this.hoverSpeed = 0;
        this.edgeAzVel = 0;
        this.edgeElVel = 0;
        this.holdActive = false;
        this.armed = false;
        this.dragging = false;
        this.canvas = null;
    }

    /**
     * Update the camera pose. Call once per frame from your render loop
     * (PlayCanvas `app.on('update', ...)` is the natural hook).
     */
    update(dt: number): void {
        // Damp velocities toward zero — produces a soft coast after release.
        if (!this.dragging) {
            this.azimuth += this.azimuthVel;
            this.elevation += this.elevationVel;
            this.azimuthVel *= this.damping;
            this.elevationVel *= this.damping;
            if (Math.abs(this.azimuthVel) < 1e-5) this.azimuthVel = 0;
            if (Math.abs(this.elevationVel) < 1e-5) this.elevationVel = 0;
        }

        // Idle auto-rotation: after `autoRotateDelay` idle seconds, orbit the
        // pivot slowly, ramping in over 2s so the start is imperceptible. Any
        // pointer/wheel/touch stamps lastInteraction and halts it instantly.
        if (this.autoRotate && !this.dragging && !this.armed) {
            const idle = (performance.now() - this.lastInteraction) / 1000 - this.autoRotateDelay;
            if (idle > 0) {
                const ramp = Math.min(1, idle / 2);
                this.azimuth += this.autoRotateSpeed * ramp * dt;
            }
        }
        // Keyboard orbit (scheme B): steer azimuth/elevation from held keys.
        // Input eases toward the target speed (~1/8 s) so a tap nudges and a
        // hold glides; velocities decay through the same lerp on release.
        // Sign convention, set by feel on Cartoixa: A = az decreases (the
        // first cut had it increasing and tested backwards), W = the camera
        // rises (el increases, looking further down).
        if (this.keyOrbit) {
            const k = this.keysDown;
            const yawIn = (k.has('d') || k.has('arrowright') ? 1 : 0) -
                (k.has('a') || k.has('arrowleft') ? 1 : 0);
            const pitchIn = (k.has('w') || k.has('arrowup') ? 1 : 0) -
                (k.has('s') || k.has('arrowdown') ? 1 : 0);
            if (yawIn !== 0 || pitchIn !== 0) {
                this.lastInteraction = performance.now();
                this.gestureKeys = true;
            }
            const ease = Math.min(1, dt * 8);
            this.keyYawVel += (yawIn * this.keyOrbit.yawSpeed - this.keyYawVel) * ease;
            this.keyPitchVel += (pitchIn * this.keyOrbit.pitchSpeed - this.keyPitchVel) * ease;
            this.azimuth += this.keyYawVel * dt;
            this.elevation += this.keyPitchVel * dt;
        }

        // Device-orientation orbit (mobile B): integrate the device's turn
        // since last frame. Turn right = orbit right (≡ D, az increases);
        // tilt the device up = see the model from lower (el decreases).
        if (this.gyroOrbit) {
            if (this.gyro.takeAlign()) {
                // First reading: the current view becomes what the device
                // held upright shows, whatever pose the scheme tap caught.
                this.gyroHomeAz = this.azimuth;
                this.gyroHomeEl = this.elevation;
                const devicePitch = this.gyro.pitch();
                if (devicePitch !== null) this.gyroElOwed = -devicePitch * this.gyroOrbit.pitchGain;
            }
            const { dHeading, dPitch } = this.gyro.consume();
            if (dHeading !== 0 || dPitch !== 0) {
                this.azimuth += dHeading * this.gyroOrbit.yawGain;
                this.elevation -= dPitch * this.gyroOrbit.pitchGain;
                this.lastInteraction = performance.now();
                if (Math.abs(dHeading) + Math.abs(dPitch) > 0.002) this.gestureRotate = true;
            }
            if (this.gyroAzOwed !== 0 || this.gyroElOwed !== 0) {
                const k = Math.min(1, dt * GYRO_ALIGN_RATE);
                const da = this.gyroAzOwed * k, de = this.gyroElOwed * k;
                this.azimuth += da;
                this.elevation += de;
                this.gyroAzOwed -= da;
                this.gyroElOwed -= de;
                this.lastInteraction = performance.now();
                if (Math.abs(this.gyroAzOwed) + Math.abs(this.gyroElOwed) < 1e-4) {
                    this.gyroAzOwed = 0;
                    this.gyroElOwed = 0;
                }
            }
        }

        // Hold-the-edge (mobile C): a finger down and still for HOLD_MS is a
        // hold; feed its position to the margin steering as the "cursor".
        if (this.edgeHold && this.armed && !this.dragging && !this.holdActive &&
            performance.now() - this.downT > HOLD_MS) {
            this.holdActive = true;
        }
        if (this.holdActive) {
            this.mouseX = this.downX;
            this.mouseY = this.downY;
            this.mouseIn = true;
        }

        // Margin steering (desktop C/D by cursor, mobile C by held finger).
        const edgeCfg = this.edgeOrbit ?? (this.holdActive ? this.edgeHold : null);
        if (edgeCfg) this.updateEdgeOrbit(dt, edgeCfg);
        else if (this.edgeHold) { this.edgeAzVel = 0; this.edgeElVel = 0; }

        // Held zooms (schemes D and E): distance multiplies by rate^(±dt) —
        // the wheel's exponential curve, spread over the hold.
        let zoomDir = 0;
        if (this.keyZoom) {
            const k = this.keysDown;
            // W = zoom in (distance shrinks), S = zoom out.
            zoomDir += (k.has('s') || k.has('arrowdown') ? 1 : 0) -
                (k.has('w') || k.has('arrowup') ? 1 : 0);
        }
        if (this.spaceZoom && this.spaceHeld) zoomDir += this.spaceDir;
        if (zoomDir !== 0) {
            const rate = (this.keyZoom ?? this.spaceZoom)!.rate;
            this.distance *= Math.pow(rate, zoomDir * dt);
            this.lastInteraction = performance.now();
            this.gestureZoom = true;
        }

        // Damp distance velocity (unused for now; wheel writes directly).
        this.distance += this.distanceVel;
        this.distanceVel *= this.damping;

        // Clamp. The floor is max(minElevation, -89°): drone scenes raise
        // minElevation above 0 so the camera can never sink below the ground.
        const elevFloor = Math.max(this.minElevation, -ELEV_CLAMP);
        if (this.softReturn) {
            // Ease back into bounds after an out-of-band external seed (tour
            // exit) instead of teleporting to the band edge.
            const k = Math.min(1, dt * 2.5);
            const clampedD = Math.min(Math.max(this.distance, this.minDistance), this.maxDistance);
            const clampedE = Math.min(Math.max(this.elevation, elevFloor), ELEV_CLAMP);
            this.distance += (clampedD - this.distance) * k;
            this.elevation += (clampedE - this.elevation) * k;
            if (Math.abs(clampedD - this.distance) < 0.01 && Math.abs(clampedE - this.elevation) < 0.001) {
                this.softReturn = false;
            }
        } else {
            if (this.elevation > ELEV_CLAMP) this.elevation = ELEV_CLAMP;
            else if (this.elevation < elevFloor) this.elevation = elevFloor;
            if (this.distance < this.minDistance) this.distance = this.minDistance;
            else if (this.distance > this.maxDistance) this.distance = this.maxDistance;
        }

        // Spherical → Cartesian. Y-up, az measured from +Z toward +X.
        this.applyPose();
    }

    /**
     * Margin steering — the walk controller's hover-look (updateEdgeLook)
     * ported to spherical coordinates: the cursor resting in a screen margin
     * drives the orbit the way scheme B's keys do. Right margin ≡ holding D
     * (azimuth increases), top margin ≡ holding W (the camera rises). Cubic
     * ease on a DC floor over the margin depth; a fast-moving cursor and the
     * toolbar's corner suppress the turn. No re-arm machinery: the schemes
     * that steer by margin have no drag to yield to.
     */
    private updateEdgeOrbit(dt: number, cfg: EdgeLookConfig): void {
        const inst = dt > 0 ? this.hoverTravel / dt : 0;
        this.hoverTravel = 0;
        this.hoverSpeed += (inst - this.hoverSpeed) * 0.3;

        let targetAz = 0;
        let targetEl = 0;
        const rect = this.canvas?.getBoundingClientRect();
        const pressed = (this.armed || this.dragging) && !this.holdActive;
        if (rect && rect.width > 0 && rect.height > 0 && this.mouseIn && !pressed) {
            const mx = this.mouseX - rect.left;
            const my = this.mouseY - rect.top;
            const inUi = mx > rect.width - cfg.uiDeadZone.w && my < cfg.uiDeadZone.h;
            if (!inUi) {
                const m = cfg.margin;
                const nx = mx / rect.width;
                const ny = my / rect.height;
                // Signed depth into the margin: 0 at its inner boundary, ±1
                // at the screen edge. Negative = left / top.
                const ax = nx < m ? -(m - nx) / m : nx > 1 - m ? (nx - (1 - m)) / m : 0;
                const ay = ny < m ? -(m - ny) / m : ny > 1 - m ? (ny - (1 - m)) / m : 0;
                const gain = 1 - Math.min(this.hoverSpeed / cfg.suppressSpeed, 1);
                const ease = (a: number) => a === 0 ? 0 : Math.sign(a) * (cfg.floor + Math.pow(Math.min(Math.abs(a), 1), cfg.ease));
                targetAz = ease(ax) * cfg.maxYaw * gain;
                targetEl = -ease(ay) * cfg.maxPitch * gain;
            }
        }

        this.edgeAzVel += (targetAz - this.edgeAzVel) * 0.15;
        this.edgeElVel += (targetEl - this.edgeElVel) * 0.15;
        if (Math.abs(this.edgeAzVel) < 1e-4) this.edgeAzVel = 0;
        if (Math.abs(this.edgeElVel) < 1e-4) this.edgeElVel = 0;
        if (this.edgeAzVel === 0 && this.edgeElVel === 0) return;

        // Actively steering — auto-rotate must not fight it.
        this.lastInteraction = performance.now();
        this.azimuth += this.edgeAzVel * dt;
        this.elevation += this.edgeElVel * dt;
        // The taught gesture is happening — lets the practice card tick "look".
        if (Math.abs(this.edgeAzVel) + Math.abs(this.edgeElVel) > 0.05) this.gestureEdge = true;
    }

    private applyPose(): void {
        const cosEl = Math.cos(this.elevation);
        const x = this.target.x + this.distance * cosEl * Math.sin(this.azimuth);
        const y = this.target.y + this.distance * Math.sin(this.elevation);
        const z = this.target.z + this.distance * cosEl * Math.cos(this.azimuth);
        this.camera.setPosition(x, y, z);
        this.camera.lookAt(this.target.x, this.target.y, this.target.z);
    }
}
